import { PlaceOfServiceCode } from 'services/constants';
import { type DiagnosisCodeOption, type ProductCodeOption } from 'store/api/referenceApi';

export const strToNumber = <T extends string | undefined | null>(val: T) =>
    val != null ? Number(val) : (undefined as T extends string ? number : undefined);

const mapToOptions = <T extends Record<string, string>>(map: T) =>
    Object.entries(map).map<{ name: string; secondaryData: keyof T; value: keyof T }>(
        ([key, value]) => ({
            name: value,
            secondaryData: key,
            value: key,
        }),
    );

export const ClaimSubmissionTypes = {
    ESTIMATE: 'ESTIMATE',
    ADJUDICATE: 'ADJUDICATE',
} as const;
export type ClaimSubmissionType = (typeof ClaimSubmissionTypes)[keyof typeof ClaimSubmissionTypes];

export const ClaimFrequencyTypes = {
    ORIGINAL: '1',
    RESUBMIT: '7',
    VOID: '8',
} as const;
export type ClaimFrequencyType = (typeof ClaimFrequencyTypes)[keyof typeof ClaimFrequencyTypes];
export const ClaimFrequencyTypeDisplayTextMap = {
    [ClaimFrequencyTypes.ORIGINAL]: 'Original',
    [ClaimFrequencyTypes.RESUBMIT]: 'Resubmit',
    [ClaimFrequencyTypes.VOID]: 'Void',
} as const;

export const ClaimPlaceOfServiceMap = {
    '11': 'Office',
    '12': 'Home',
    '21': 'Inpatient Hospital',
    '22': 'Outpatient Hospital',
    '23': 'Emergency Room - Hospital',
} as const;
export const ClaimPlaceOfServiceOptions = mapToOptions(ClaimPlaceOfServiceMap).filter(
    // Only allow '11' and '12' to be entered in the UI
    ({ secondaryData }) => ['11', '12'].includes(secondaryData),
);

export const ClaimPlaceOfServiceReferralPlaceOfServiceMap = {
    '11': PlaceOfServiceCode.PHYS_SPEC,
    '12': PlaceOfServiceCode.HOME,
    '21': PlaceOfServiceCode.FACILITY,
    '22': PlaceOfServiceCode.FACILITY,
    '23': PlaceOfServiceCode.FACILITY,
} as const;

export const ProviderAcceptAssignmentMap = {
    A: 'Assigned',
    B: 'Assignment Accepted on Clinical Lab Services Only',
    C: 'Not Assigned',
} as const;
export const ProviderAcceptAssignmentOptions = mapToOptions(ProviderAcceptAssignmentMap);

export const ReleaseOfInformationMap = {
    I: 'Informed Consent to Release Medical Information for Conditions or Diagnoses Regulated by Federal Statutes',
    Y: 'Yes, Provider has a Signed Statement Permitting Release of Medical Billing Data Related to a Claim',
} as const;
export const ReleaseOfInformationOptions = mapToOptions(ReleaseOfInformationMap);

export const ClaimUomMap = {
    F2: 'International Units',
    GR: 'Grams',
    ME: 'Milligrams',
    ML: 'Milliliters',
    UN: 'Units',
} as const;
export const ClaimUomOptions = mapToOptions(ClaimUomMap);

// No adjustments come back for batch adjudicate submissions, so set values to undefined
export const checkBatchSubmission = (
    adjustment: string | undefined,
    payerId: string,
    isEstimate: boolean,
) => (isEstimate || payerId !== 'HM001' ? strToNumber(adjustment) : undefined);

export interface ClaimSubmissionFormSchema {
    originalClaimNumber?: string;
    supplierNpi: string;
    taxonomyCode: string;
    placeOfService: (typeof ClaimPlaceOfServiceOptions)[0];
    memberId: string;
    patientAccountNumber: string;
    providerSignatureOnFile: string;
    providerAcceptAssignment: (typeof ProviderAcceptAssignmentOptions)[0];
    assignmentOfBenefits: string;
    releaseOfInformation: (typeof ReleaseOfInformationOptions)[0];
    primaryDiagnosis: DiagnosisCodeOption;
    additionalDiagnoses: DiagnosisCodeOption[];
    services: {
        key: string;
        dateOfService: [Date, Date];
        procedure: ProductCodeOption;
        modifierCodes?: string;
        ndc?: ProductCodeOption;
        diagnoses: DiagnosisCodeOption[];
        billingQty: number;
        dispensedQty?: number;
        dispensedQtyUom?: (typeof ClaimUomOptions)[0];
        providerCharge: number;
    }[];
}
